import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { AlertController, ModalController } from '@ionic/angular';
import { AuftragService } from 'src/app/api/auftrag.service';
import { Logger } from 'src/app/api/helper/app-error-logger';
import { AppStorage } from 'src/app/api/helper/app-storage';
import { AppConfig } from 'src/app/api/helper/app.config';
import { UiHelper } from 'src/app/api/helper/ui-helper';
import { Utils } from 'src/app/api/helper/utils';
import { Auftragsstatus } from 'src/app/api/model/aufteagsstatus';
import { AuftragsKomponente, Unterschrift } from 'src/app/api/model/swagger-model';
import { AuftragUnterschriftPage } from '../../auftrag-unterschrift/auftrag-unterschrift.page';
import { Auftragdetails } from '../auftragdetails.helper';
import { UnterschriftVO } from '../auftragdetails.model';
import { KomponenteBase } from './komponente';
import { NeutralWorkflowAktuelleAnsichtTyp } from 'src/app/api/model/model';
import { MyApp } from 'src/app/api/helper/app';

const log = new Logger("AuftragUnterschriftComponent");

@Component({
    selector: 'auftrag-unterschrift-komponente',
    templateUrl: 'unterschrift-komponente.component.html',
    styleUrls: ['./unterschrift-komponente.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AuftragUnterschriftComponent extends KomponenteBase implements OnInit {

    @Input() auftragdetails: Auftragdetails;
    @Input() komponente: AuftragsKomponente;

    unterschriften: UnterschriftVO[];

    unterschriftLaeuft = false;

    constructor(
        private router: Router,
        private alertController: AlertController,
        private modalController: ModalController,
        public domSanitizationService: DomSanitizer,
        protected cdr: ChangeDetectorRef) {
        super(cdr);
    }

    ngOnInit() {
        super.ngOnInit();

        this.aktualisiereUnterschriften();

        this.subscriptions.push(this.auftragdetails.neutralAktuelleGeaendert.subscribe(() => {
            this.aktualisiereUnterschriften();
            this.cdr.detectChanges();
        }));
    }

    async onStarteUnterschrift(rolle: string) {
        try {
            if (this.unterschriftLaeuft) {
                // Doppelt geklickt
                return;
            }

            this.unterschriftLaeuft = true;
            this.cdr.detectChanges();

            MyApp.loading(true);

            await this.starteUnterschrift(rolle);
        } catch (err) {
            log.error('onStarteUnterschrift: ' + Utils.getErrorMessage(err), err);
        } finally {
            this.unterschriftLaeuft = false;
            this.cdr.detectChanges();

            MyApp.loading(false);
        }
    }

    async aktualisiereUnterschriften() {
        if (!this.auftrag) {
            return;
        }

        const workflow = this.auftragdetails.workflow;

        // const unterschriftKomponente = this.formularKonfiguration.komponenten.find(p => p.name == 'Unterschrift');

        if (this.unterschriften && this.unterschriften.length) {
            // Unterschriften-Objekte wurden schon geladen. Müssen nur aktualisiert werden
            for (const vo of this.unterschriften) {
                this.aktualisiereUnterschriftVO(vo);
            }
        } else {
            // Neu initialisieren
            this.unterschriften = [];

            if (workflow && workflow.einstellungen.Unterschrift !== 'nein') {
                // Unterschrift-Komponente ist im Formular vorhanden und im Workflow auch aktiv

                this.addUnterschriftVO('ERZ');
                this.addUnterschriftVO('BEF');
                this.addUnterschriftVO('ENT');
            }
        }

        if (workflow?.einstellungen?.Neutral === 'ja') {
            if (this.auftragdetails.neutralAktuelleAnsicht === NeutralWorkflowAktuelleAnsichtTyp.Entsorger) {
                // Unterschrift vom Erzeuger ausblenden. Das muss der Entsorger nicht sehen
                this.setUnterschriftHidden('ERZ', true);
                this.setUnterschriftHidden('ENT', false);
            } else {
                // Es soll der Erzeuger auch das Unterschriftenfled für den Entsorger nicht sehen uns andersrum
                this.setUnterschriftHidden('ERZ', false);
                this.setUnterschriftHidden('ENT', true);
            }
        }
    }

    setUnterschriftHidden(rolle: string, hidden: boolean) {
        const vo = this.unterschriften.find(p => p.rolle === rolle);

        if (vo) {
            vo.hidden = hidden;
        }
    }

    addUnterschriftVO(rolle: string) {
        const rolleFormularEinstellung = this.komponente.einstellungen.find(p => p.key == rolle);

        if (rolleFormularEinstellung) {
            const vo = new UnterschriftVO();

            if (this.auftrag?.UnterschriftKundeErforderlich && rolle === 'ERZ') {
                vo.erforderlich.next(true);
            } else if (rolleFormularEinstellung.wert === 'ja') {
                vo.erforderlich.next(true);
            } else if (rolleFormularEinstellung.wert === 'optional') {
                vo.erforderlich.next(false);
            } else {
                return;
            }

            vo.rolle = rolle;

            vo.height = this.getEinstellung('Height');

            if (!vo.height) {
                vo.height = undefined;
            }

            this.aktualisiereUnterschriftVO(vo);

            this.unterschriften.push(vo);
        }
    }

    aktualisiereUnterschriftVO(vo: UnterschriftVO) {
        const unterschrift = this.auftrag.Unterschriften.find(p => p.Typ == vo.rolle);

        vo.unterschrift.next(unterschrift);

        if (unterschrift) {
            vo.bild = unterschrift.Bild;
            vo.name = unterschrift.Name;

            if (unterschrift.Bild) {
                vo.vorhanden = true;
            } else {
                vo.vorhanden = false;
            }
        } else {
            vo.bild = '';
            vo.name = '';
            vo.vorhanden = false;
        }
    }

    async starteUnterschrift(rolle: string) {
        UiHelper.blurActiveElement();

        if (this.auftragdetails.isUnterschreibenGesperrt) {
            return;
        }

        if (!await this.auftragdetails.pruefeZeiterfassungGestartet()) {
            return;
        }

        if (!rolle) {
            // TODO: Benutzer auswählen lassen. Wird das überhaupt noch benötigt??

            // Loading-Indicator ausblenden
            MyApp.loading(false);

            rolle = await this.frageNachUnterschriftRolle();
        }

        if (!rolle) {
            return;
        }

        let unterschrift = this.auftrag.Unterschriften.find(p => p.Typ == rolle);

        if (!unterschrift) {
            unterschrift = {
                Bild: null,
                Datum: null,
                Guid: null,
                Name: '',
                Typ: rolle,
            };

            this.auftrag.Unterschriften.push(unterschrift);
        }

        if (unterschrift.Bild) {
            this.auftragdetails.alertAktiv = true;

            // Loading-Indicator ausblenden
            MyApp.loading(false);

            // Unterschrift schon vorhanden. Rückfrage ob gelöscht oder neu unterschrieben werden soll
            const aktion = await this.frageVorhandeneUnterschriftAktion(unterschrift);

            this.auftragdetails.alertAktiv = false;

            if (aktion === 'loeschen') {
                // Unterschrift löschen und abbrechen
                unterschrift.Bild = null;
                unterschrift.Datum = null;
                unterschrift.Guid = null;
                unterschrift.Name = null;

                await this.aktualisiereUnterschriften();

                await AuftragService.instance.speichereAuftrag(this.auftrag);

                this.auftragdetails.updateAuftragReadOnly();

                this.auftragdetails.detectChanges.next('unterschrift-loeschen');

                return;
            } else if (aktion === 'neu') {
                // Neu unterschrieben. Einfach weiter
            } else {
                // Abbruch
                return;
            }
        }

        let name = unterschrift.Name;

        if (!name) {
            if (rolle == 'ERZ') {
                if (this.auftrag.Abholadresse.Ansprechpartner) {
                    name = this.auftrag.Abholadresse.Ansprechpartner;
                }
            } else {
                name = await AppStorage.current.get('name-unterschrift-' + rolle, true, true);
            }
        }

        const lautschriftErforderlich = AppConfig.current.einstellungen['UnterschriftLautschrift' + rolle] !== 'nein';

        if (!lautschriftErforderlich) {
            unterschrift.Name = this.getEinstellung(rolle + '-Titel', rolle);

            await AuftragService.instance.speichereAuftrag(this.auftrag);

            await this.auftragdetails.starteAuftragWennErforderlich('starteUnterschrift: ' + rolle);

            const modal = await this.modalController.create({
                component: AuftragUnterschriftPage,
                componentProps: {
                    'auftrag': this.auftrag,
                    'rolle': rolle
                },
                cssClass: 'modal-fullscreen'
            });

            log?.debug('AuftragUnterschriftPage wird angezeigt...');
            await modal.present();

            // Loading-Indicator ausblenden
            MyApp.loading(false);

            const modalResult = await modal.onDidDismiss();
            log?.debug('AuftragUnterschriftPage wurde geschlossen', modalResult);

            await Utils.delay(100);

            this.aktualisiereUnterschriften();

            await this.auftragdetails.starteAuftragWennErforderlich('Unterschrift ' + rolle);

            this.auftragdetails.updateAuftragReadOnly();

            this.auftragdetails.detectChanges.next('starteUnterschrift');

            // await this.router.navigateByUrl('/auftragsliste/details/' + this.auftrag.Key + '/unterschrift/' + rolle);

            return;
        }

        this.auftragdetails.alertAktiv = true;

        // Loading-Indicator ausblenden
        MyApp.loading(false);

        const alert = await this.alertController.create({
            cssClass: 'input-alert',
            message: 'Vollständiger Name',
            inputs: [
                {
                    name: 'name',
                    type: 'text',
                    handler: (input) => { log?.debug('input', input); },
                    placeholder: '',
                    value: name
                }
            ],
            buttons: [
                {
                    text: 'Abbruch',
                    role: 'cancel',
                    handler: () => {
                        this.auftragdetails.alertAktiv = false;
                        // ok. Unterschrift abgebrochen
                    }
                },
                {
                    text: 'OK',
                    role: 'accept',
                    handler: async data => {
                        if (!data.name || !data.name.trim()) {
                            return false;
                        }

                        this.auftragdetails.alertAktiv = false;

                        try {
                            if (unterschrift.Name != data.name) {
                                unterschrift.Bild = '';
                            }

                            unterschrift.Name = data.name;

                            MyApp.loading(true);

                            await AppStorage.current.set('name-unterschrift-' + rolle, unterschrift.Name, true, true);

                            await AuftragService.instance.speichereAuftrag(this.auftrag);

                            const modal = await this.modalController.create({
                                component: AuftragUnterschriftPage,
                                componentProps: {
                                    'auftrag': this.auftrag,
                                    'rolle': rolle
                                },
                                cssClass: 'modal-fullscreen'
                            });

                            await modal.present();

                            // Loading-Indicator ausblenden
                            MyApp.loading(false);

                            await modal.onWillDismiss();

                            this.aktualisiereUnterschriften();

                            this.auftragdetails.updateAuftragReadOnly();

                            this.auftragdetails.detectChanges.next('starteUnterschrift');

                            // await this.router.navigateByUrl('/auftragsliste/details/' + this.auftrag.Key + '/unterschrift/' + rolle);
                        } catch (err) {
                            log.error('handler', err);

                            // Loading-Indicator ausblenden
                            MyApp.loading(false);
                        }
                    }
                }
            ]
        });

        await alert.present();

        const firstInput: any = document.querySelector('ion-alert input');

        if (firstInput) {
            firstInput.focus();
        }

        await alert.onWillDismiss();

        this.auftragdetails.alertAktiv = false;
    }

    frageNachUnterschriftRolle(): Promise<string> {
        return new Promise(async resolve => {
            const buttons: any[] = [];

            for (const vo of this.unterschriften) {
                buttons.push({
                    text: vo.getRolleLang() + '(' + vo.rolle + ')',
                    icon: 'create',
                    handler: () => {
                        resolve(vo.rolle);
                    }
                });
            }

            buttons.push({
                text: 'Abbruch',
                icon: 'close',
                role: 'cancel',
                handler: () => {
                    resolve(null);
                }
            });

            const alert = await this.alertController.create({
                header: 'Rolle wählen',
                cssClass: 'no-text-alert',
                mode: 'ios',
                buttons: buttons
            });

            await alert.present();
        });
    }

    frageVorhandeneUnterschriftAktion(unterschrift: Unterschrift): Promise<string> {
        return new Promise(async resolve => {
            const buttons: any[] = [];

            if (this.auftrag.Auftragsstatus >= Auftragsstatus.Abgeschlossen) {
                resolve(null);
                return;
            }

            buttons.push({
                text: 'Löschen',
                role: 'destructive',
                icon: 'trash',
                handler: () => {
                    resolve('loeschen');
                }
            });

            buttons.push({
                text: 'Neu unterschreiben',
                icon: 'create',
                handler: () => {
                    resolve('neu');
                }
            });

            buttons.push({
                text: 'Abbruch',
                icon: 'close',
                role: 'cancel',
                handler: () => {
                    resolve(null);
                }
            });

            const alert = await this.alertController.create({
                cssClass: 'no-text-alert no-title-alert',
                mode: 'ios',
                buttons: buttons
            });

            await alert.present();

            // await alert.onDidDismiss();
        });
    }
}
